import type { DifferenceClass, UpgradeVerdictStatus } from './review-types'
import { UpdateReportAdTypes } from '../../util/contstants'

export const upgradeNoun = (adTypes: number[]) => {
    const types = [...new Set(adTypes)]
    if (types.length !== 1) return 'items'
    const nouns = new Map<number, string>([
        [UpdateReportAdTypes.Campaigns || 1, 'campaigns'],
        [UpdateReportAdTypes.AdGroups || 2, 'ad groups'],
        [21, 'ads'],
        [UpdateReportAdTypes.Keywords || 4, 'keywords'],
        [UpdateReportAdTypes.Targets || 5, 'targets'],
    ])
    return nouns.get(Number(types[0])) ?? 'items'
}

export const REVIEW_COPY = {
    title: (count: number) => `Upgrading ${count} Recipe${count === 1 ? '' : 's'} to Segments`,
    singleTitle: (name: string) => `Upgrade "${name}"`,
    checked: (date: string) => `Checked ${date}`,
    itemsMatch: (matches: number, total: number, adTypes: number[] = []) =>
        `${matches} of ${total} ${upgradeNoun(adTypes)} match exactly.`,
    reassurance:
        'We copy each Recipe to a new Segment and turn off the original. Nothing is deleted, and you can turn the original back on at any time.',
    noRecipes: 'There are no V1 Recipes to upgrade.',
    pending:
        "We're still checking this Recipe against your live data. This page updates automatically.",
    upgradeProgress: (upgraded: number, total: number) =>
        `${upgraded} of ${total} Recipes upgraded. The rest are still upgrading. Refresh the page in a minute to see them.`,
    unparseable:
        'This Recipe has an error in its trigger, so it has never run. Fix the trigger in the editor to upgrade it.',
    items: {
        loading: 'Loading items…',
        truncated: (shown: number, count: number) =>
            `Showing the first ${shown} of ${count} items.`,
        unavailable:
            'This check ran before item lists were available. The item list appears after this Recipe is checked again.',
        error: "We couldn't load the items. Try again.",
        retry: 'Try again',
        nameMissing: 'Name not available',
        id: (entityId: string) => `ID ${entityId}`,
    },
    errors: {
        upgrade_not_ready:
            'This Recipe is still being checked. Try again when the check is complete.',
        recipe_not_v1: "This Recipe is no longer a V1 Recipe and can't be upgraded.",
        profile_mismatch: 'This Recipe belongs to another ad account.',
        unparseable:
            'This Recipe has an error in its trigger. Fix the trigger before upgrading it.',
        trigger_changed:
            'This Recipe changed after it was checked. Check it again before upgrading it.',
    },
} as const

const plural = (count: number, one: string, many: string) => (count === 1 ? one : many)

export const DIFFERENCE_COPY: Record<DifferenceClass, (count: number) => string> = {
    entities_missing_from_v2: (n) =>
        `${plural(n, 'item', 'items')} that matched the Recipe ${plural(n, "doesn't", "don't")} match the Segment today.`,
    entities_extra_in_v2: (n) =>
        `more ${plural(n, 'item matches', 'items match')} the Segment because it uses current effective values.`,
    inherited_bid_uses_effective_default: (n) =>
        `${plural(n, 'item inherits its', 'items inherit their')} ad group's bid, so the Segment uses that effective bid.`,
    v2_safety_clamp: (n) =>
        `${plural(n, 'value is', 'values are')} kept within Merch Jar's current safety limits.`,
    deterministic_weighted_rounding_drift: (n) =>
        `${plural(n, 'value differs', 'values differ')} by one cent because weighted calculations now round consistently.`,
    action_value_mismatch: (n) =>
        `${plural(n, 'item matches, but its', 'items match, but their')} calculated values differ.`,
}

export const STATUS_COPY: Record<UpgradeVerdictStatus, string> = {
    pending: 'Checking',
    ok: 'Identical',
    differences: 'Differences',
    unparseable: 'Needs Fixing',
    upgraded: 'Upgraded',
}
